import { googleSignOn } from './googleSignOn';
import { GoogleSignInRenderOptions, isLegacyGoogleSetting } from './socialSignOnTypes';
import Identity from '../sdk/identity';

import { deprecationMessage } from './googleSignOn';

/** @hidden */
export const GOOGLE_SDK_URL = 'https://apis.google.com/js/platform.js';

/** @hidden */
export const loadGoogleAuthSDK = (init?: Function): Promise<void> => {
  console.error(deprecationMessage);
  return new Promise(async (resolve, reject) => {
    if (!document.querySelector(`[src="${GOOGLE_SDK_URL}"]`)) {
      const element = document.createElement('script');

      element.onload = function () {
        window.gapi.load('auth2', async () => {
          Identity._google = {
            ...Identity._google,
            auth2:
              window.gapi?.auth2 ??
              (isLegacyGoogleSetting(Identity._google) && Identity._google?.auth2)
          };
          if (init) {
            try {
              await init();
            } catch (e) {
              return reject(e);
            }
          }
          return resolve();
        });
      };

      element.onerror = function () {
        reject('[Arc Identity SDK] - Error loading Google SDK');
      };

      element.async = true;
      element.src = GOOGLE_SDK_URL;
      document.head.appendChild(element);
    } else {
      if (!window.gapi) {
        return reject('[Arc Identity SDK] - Error loading Google SDK');
      }
      if (!window.gapi.auth2) {
        window.gapi.load('auth2', async () => {
          if (init) {
            try {
              await init();
            } catch (e) {
              return reject(e);
            }
          }
          return resolve();
        });
      } else {
        if (init) {
          try {
            await init();
          } catch (e) {
            return reject(e);
          }
        }
        return resolve();
      }
    }
  });
};

/** @hidden */
export const getGoogleAuthInstance = (): Promise<void> => {
  console.error(deprecationMessage);
  return new Promise(async (resolve, reject) => {
    if (isLegacyGoogleSetting(Identity._google)) {
      if (Identity._google.authInstance) {
        resolve();
      } else {
        if (Identity._google.auth2) {
          Identity._google.authInstance = Identity._google.auth2.getAuthInstance();
          resolve();
        } else {
          try {
            await initGoogleAuthSDK(Identity._google.clientId);
            resolve();
          } catch (e) {
            console.error('Error getting Google Auth instance.');
            reject(e);
          }
        }
      }
    } else {
      reject(
        'This method should only be used with the legacy Google Sign In. Migrate to the new Sign In With Google'
      );
    }
  });
};

/**
 * This SDK initializes the Google Auth2 JS SDK with the provided Google clientId or the Google clientId configured in
 * Arc Identity. Note: this method assumes the Google 'gapi' javascript SDK is ålready loaded on the page & the `gapi.auth2`
 * SDK is loaded. Optionally, provide
 *
 * @param clientId optional clientId to initialize Google Auth2 SDK with
 */
export const initGoogleAuth = async (clientId?: string) => {
  console.error(deprecationMessage);

  Identity._google = {
    ...Identity._google,
    auth2:
      window.gapi?.auth2 ?? (isLegacyGoogleSetting(Identity._google) && Identity._google?.auth2),
    clientId:
      clientId ?? (isLegacyGoogleSetting(Identity._google) ? Identity._google?.clientId : undefined)
  };
  if (!Identity._google?.auth2) {
    throw new Error('[Arc Identity SDK] - Error loading Google SDK');
  }

  if (
    !Identity._google?.clientId &&
    (!Identity.configOptions || (Identity.configOptions && !Identity.configOptions.googleClientId))
  ) {
    await Identity.getConfig();
  }

  Identity._google.clientId =
    Identity._google?.clientId ?? Identity.configOptions?.googleClientId?.split(',')?.[0];

  if (!Identity._google.clientId) {
    throw new Error(
      '[Arc Identity SDK] - Google Client ID not found, please add one in settings or explicitly pass one to the initGoogleLogin method.'
    );
  } else {
    try {
      const googleAuthInstance =
        Identity._google.authInstance ?? Identity._google.auth2.getAuthInstance();
      if (!googleAuthInstance) {
        const googleAuth = Identity._google.auth2.init({
          client_id: `${Identity._google.clientId.replace(
            '.apps.googleusercontent.com',
            ''
          )}.apps.googleusercontent.com`
        });
        Identity._google.authInstance = googleAuth;
      } else {
        Identity._google.authInstance = googleAuthInstance;
      }
    } catch (e) {
      console.error('Error while initializing Google sdk:');
      throw e;
    }
  }
};

/**
 * Loads and initializes the Google SDK in your application.
 * @param clientId clientId the client id given to you by Google associated with your developer account - optional if you'd like to pass in manually. If you do not pass an id and one is already configured for your organization, the SDK will use that one by default.
 */
export const initGoogleAuthSDK = async (clientId?: string) => {
  console.error(deprecationMessage);

  if (isLegacyGoogleSetting(Identity._google)) {
    if (clientId && clientId !== Identity._google?.clientId) {
      Identity._google.clientId = clientId;
    }
    const id = Identity._google.clientId;
    try {
      await loadGoogleAuthSDK(() => initGoogleAuth(id));
    } catch (e) {
      throw e;
    }
  } else {
    throw new Error(
      'This method should only be used with legacy Google Sign In. Discontinue use & migrate to new Sign In With Google'
    );
  }
};

/**
 * @deprecated
 * Loads and initializes the Google SDK in your application & optionally renders the sign on with Google button
 *
 * @param clientId clientId the client id given to you by Google associated with your developer account - optional if you'd like to pass in manually. If you do not pass an id and one is already configured for your organization, the SDK will use that one by default.
 * @param recaptchaToken token returned by recaptcha. It is required in case reCAPTCHA is enabled as part of the settings for SignIn or Login.
 * @param renderOptions when specified, will render the Google sign in button to the dom element with the buttonId provided. Refer to this documentation for options: https://developers.google.com/identity/sign-in/web/reference#gapisignin2renderid-options. `scope` will be ignored. defaults to
 * ```json
 * {
 *  width: 240,
 *  height: 50,
 *  longtitle: true,
 *  theme: 'dark'
 * }
 *  ```
 * @param [buttonId='google-sign-in-button'] the id of the HTML element you want to render the google button into.
 * @param customButton specify true if you want to use a custom 'Login with Google' button. The button must be passed [[googleSignOn]] as its click handler.

 *
 */
export async function initGoogleLogin(
  clientId: string = '',
  renderOptions: GoogleSignInRenderOptions = {
    width: 240,
    height: 50,
    longtitle: true,
    theme: 'dark',
    scope: ['profile', 'email']
  },
  buttonId: string = 'google-sign-in-button',
  customButton?: boolean
): Promise<void> {
  console.error(deprecationMessage);

  try {
    if (isLegacyGoogleSetting(Identity._google)) {
      if (clientId && clientId !== Identity._google.clientId) {
        Identity._google.clientId = clientId;
      }

      if (!Identity._google.auth2 || !Identity._google.authInstance) {
        await initGoogleAuthSDK(Identity._google.clientId);
      }

      if (!Identity._google.authInstance) {
        throw new Error('[Arc Identity SDK] - Error initializing Google Auth2 SDK');
      }

      const successHandler = (googleUser: any) => {
        googleSignOn(googleUser).then(() => {
          if (renderOptions && typeof renderOptions.onSuccess === 'function') {
            renderOptions.onSuccess(googleUser);
          }
        });
      };

      if (renderOptions && typeof renderOptions.onSuccess === 'function') {
        Identity._google.onSuccess = renderOptions.onSuccess;
        if (typeof renderOptions.onFailure === 'function') {
          Identity._google.onFailure = renderOptions.onFailure;
        }
      }

      if (customButton) {
        Identity._google.useCustomButton = true;
      }

      // Attaches a click handler manually. This is done manually because in applications where
      // the HTML is mounted & unmounted without reloading the page, the onSuccess function
      // sometimes fails to fire.
      if (!customButton && renderOptions) {
        Identity._google.authInstance.attachClickHandler(
          buttonId,
          {
            scope: (renderOptions?.scope ?? ['profile', 'email']).join(' '),
            prompt: 'select_account'
          },
          successHandler,
          (renderOptions && renderOptions.onFailure) || console.error
        );

        const defaultRenderOptions = {
          width: 240,
          height: 50,
          longtitle: true,
          theme: 'dark'
        };

        const options = {
          ...defaultRenderOptions,
          ...renderOptions,
          scope: (renderOptions?.scope ?? ['profile', 'email']).join(' ')
        };

        // renders default button w/ render options.
        return window.gapi.signin2.render(buttonId, options);
      }
    } else {
      throw new Error(
        '[Arc Identity SDK] - This method will only work with the legacy Google Sign In. Discontinue use of this method.'
      );
    }
  } catch (e) {
    console.error('Error while initializing Google login:');
    throw e;
  }
}

export default initGoogleLogin;
